import { orderApi } from 'api/app/order/api';
import type {
  B2BDeliveryOrder,
  B2BOrderArgs,
  B2BOrderItem,
} from 'api/app/order/types';
import { selectRecipientInfo, selectToken } from 'features/auth/slice';
import { debounce, groupBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { FORCE_PAY_BY_BALANCE_STORE_IDS } from 'shared/constants/common';
import { useTypedSelector } from 'shared/redux/store';
import type { AddToCartFormValues } from '../../ItemDetails/types';
import { useMyLocation } from '../../Location/hooks/useMyLocation';
import { usePromoContext } from '../components/PromoCode/contexts/PromoContext/PromoContext';
import {
  selectAhaPromo,
  selectCartItems,
  selectPaymentMethod,
  selectSuggestServices,
} from '../slice';

export const useEstimateFeeArgs = (): B2BOrderArgs<
  Omit<B2BDeliveryOrder, 'total_pay' | 'e_invoice'>
> | null => {
  const myLocation = useMyLocation();
  const token = useTypedSelector(selectToken);
  const recipientInfo = useTypedSelector(selectRecipientInfo);

  const aha_promo = useTypedSelector(selectAhaPromo);
  const payment_method = useTypedSelector(selectPaymentMethod);
  const cartItems = useTypedSelector(selectCartItems);
  const suggestServices = useTypedSelector(selectSuggestServices);

  return useMemo(() => {
    if (!token || !recipientInfo || !myLocation) return null;

    const groupedCartItems = groupBy(cartItems, (item) => item.store_id);

    const delivery_orders: Omit<B2BDeliveryOrder, 'total_pay' | 'e_invoice'>[] =
      [];

    Object.keys(groupedCartItems).forEach((storeId) => {
      const suggestService = suggestServices.find(
        (s) => s.store_id === +storeId
      );

      const deliveryOrder = {
        store_id: +storeId,
        need_confirm: !groupedCartItems[storeId][0].auto_confirm,
        idle_until: groupedCartItems[storeId][0].idle_until,
        path: [
          {
            address: groupedCartItems[storeId][0].store_path.address,
            lat: groupedCartItems[storeId][0].store_path.lat,
            lng: groupedCartItems[storeId][0].store_path.lng,
            name: groupedCartItems[storeId][0].store_path.name,
            mobile: groupedCartItems[storeId][0].store_path.mobile,
          },
          {
            address: myLocation.address,
            lat: myLocation.lat,
            lng: myLocation.lng,
            ...recipientInfo,
          },
        ],
        items: groupedCartItems[storeId].map(
          (item: AddToCartFormValues) =>
            ({
              _id: item._id,
              store_id: item.store_id,
              name: item.name,
              num: item.num,
              price: item.price * item.num,
              unit: item.unit,
              model_id: item.tier_id,
              model_name: item.tier_name,
            } as B2BOrderItem)
        ),
      } as Omit<B2BDeliveryOrder, 'total_pay' | 'e_invoice'>;

      if (suggestService) {
        const { service_id, request } = suggestService;
        if (service_id) {
          deliveryOrder.service_id = service_id;
        }
        if (request && request._id) {
          deliveryOrder.requests = [request] as B2BDeliveryOrder['requests'];
        } else {
          deliveryOrder.requests = [];
        }
      }
      delivery_orders.push(deliveryOrder);
    });

    return {
      aha_promo,
      payment_method,
      delivery_orders,
    };
  }, [
    aha_promo,
    myLocation,
    payment_method,
    token,
    recipientInfo,
    suggestServices,
    // cartItems,
  ]);
};

export const useEstimateFeeState = () => {
  return orderApi.endpoints.estimateB2BOrderFee.useMutation({
    fixedCacheKey: 'estimate-fee',
  });
};

export const useEstimateFee = () => {
  const orderArgs = useEstimateFeeArgs();

  const { closePromoDialog } = usePromoContext();

  const [estimateFeeTrigger, result] = useEstimateFeeState();

  const isEstimateFeeError = !!result.data?.error || !!result.error;

  const disablePayViaCash = !!(
    orderArgs &&
    orderArgs.delivery_orders.some((o) =>
      FORCE_PAY_BY_BALANCE_STORE_IDS.includes(o.store_id)
    ) &&
    orderArgs.payment_method === 'cash_by_recipient'
  );

  const errMessageEstimateFee = result.isError
    ? result.error &&
      (result.error as any).data &&
      `${
        (result.error as any).data.description ||
        (result.error as any).data.title
      }`
    : isEstimateFeeError
    ? result.data?.error?.description || result.data?.error?.title
    : null;

  const estimateFeeFn = useCallback(
    (args: typeof orderArgs) => {
      if (
        args &&
        args.payment_method &&
        args.delivery_orders &&
        args.delivery_orders.length > 0 &&
        args.delivery_orders.some((d) => !!d.service_id)
      ) {
        estimateFeeTrigger(args).then((res) => {
          if (!(res as any)?.data?.promo_error_message && args.aha_promo) {
            closePromoDialog();
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [estimateFeeTrigger]
  );

  const debounceEstimateFeeFn = useMemo(
    () =>
      debounce((args: typeof orderArgs) => {
        estimateFeeFn(args);
      }, 250),
    [estimateFeeFn]
  );

  useEffect(() => {
    debounceEstimateFeeFn(orderArgs);
  }, [debounceEstimateFeeFn, orderArgs]);

  const estimateFee = useMemo(
    () => () => {
      estimateFeeFn(orderArgs);
    },
    [orderArgs, estimateFeeFn]
  );

  const debounceEstimateFee = useMemo(
    () => () => {
      debounceEstimateFeeFn(orderArgs);
    },
    [orderArgs, debounceEstimateFeeFn]
  );

  return {
    orderArgs,
    estimateFee,
    debounceEstimateFee,
    result,
    isEstimateFeeError,
    errMessageEstimateFee,
    disablePayViaCash,
  };
};
